import { ChevronDown, FileText, Loader2, Paperclip, Send, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, Textarea } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  CHANNEL_LABELS, COMPOSER_CHANNELS, composeRefusal, composerReason, conversationTitle, emailTarget,
  initialComposerChannel, smsBody, smsRoom, smsSegments, smsTarget, type Channel, type Conversation,
  type EmailTarget, type SmsTarget,
} from "@/lib/conversations";
import {
  MMS_ACCEPT, MMS_MAX_FILES, MMS_TOTAL_MAX_BYTES, formatBytes, mmsFileProblem,
} from "@/lib/mms";
import {
  useAddNote, useContactTextCompose, useSendText, useTextCompose, useTextContact,
  useUploadConversationMedia,
} from "@/lib/queries";
import { sendFailure } from "@/lib/texts";
import type { LeadCard } from "@/lib/types";
import { cn, formatDateTime, formatPhoneNumber } from "@/lib/utils";
import { RecordContext } from "@/features/conversations/activity-rows";
import { CHANNEL_ICONS, CHANNEL_TONES } from "@/features/conversations/channel-ui";
import { useEmailComposeFor, useSendEmailTo } from "@/features/conversations/email-compose";

/**
 * Why a channel cannot carry a reply to this contact, or null when only sending itself is missing.
 *
 * Each channel is asked about its own way of reaching them, never the other's: a lead with an SMS
 * thread opened on Email, or an email thread opened on SMS, is not held back by the row it was
 * opened from. Email asks nothing about an address here: core-api's compose answer says whether
 * there is one it may use (none, changed since the thread began, hidden from this viewer). SMS on
 * an email thread reads the phone from the contact's record, once it has answered.
 */
function blockedFor(c: Conversation, channel: Channel, record: LeadCard | undefined): string | null {
  if (channel === "comment") return null;
  if (channel !== "email") {
    // Until the record has answered, nothing is said: the text's own compose answer will say it too.
    const noPhone = c.channel === "email" ? !c.leadId || (record !== undefined && !record.phone) : !c.phone;
    if (noPhone) return "No phone number for this contact";
  }
  const dndKey = channel === "sms" || channel === "whatsapp" || channel === "imessage" || channel === "email" ? channel : null;
  if (c.contact.dnd.all || (dndKey && c.contact.dnd[dndKey])) return "Do not disturb is on for this channel";
  return null;
}

/**
 * Where a reply is written: a channel, and a box shaped by it. Email gets a subject; SMS counts its
 * segments; an internal comment turns the box the team-only colour and says the contact never sees
 * it. The channel starts as the one the contact last wrote on.
 *
 * An internal comment is a note on the contact's record (`POST /leads/{id}/notes`): it lands in
 * the thread, and on the record's timeline, as soon as it is saved. A thread with no record cannot
 * take one.
 *
 * An SMS (ADR-098, ACL-330) is a reply in core-api's conversation, from the number the thread is on,
 * or — on a contact's thread before they have one — a text from their record, which starts it.
 * Whether it may leave is core-api's to say, before anything is typed (the compose routes): quiet
 * hours on their clock, a STOP, do-not-call, no consent, a number not set up, the day's limit. Send
 * is off with that sentence until it may. A send holds the button until core-api answers, as a
 * request sent twice is two texts. A text core-api kept and the provider then refused is in the
 * thread as not delivered; one it would not keep is a toast with why. A send whose answer is lost (a
 * 5xx, a dropped connection) may have been kept and sent all the same, so it is not offered again:
 * the box clears, and the toast says to check the thread first.
 *
 * A text can carry pictures and PDFs (ADR-140, ACL-503), on SMS and on a thread core-api already
 * has: each is uploaded as it is chosen, and the send names the ones that are stored. Twilio caps a
 * message and all its media together at 5 MB, so the strip shows the running total rather than
 * checking each file on its own — four 2 MB photos are each fine and together are a send that fails
 * after every upload (`lib/mms.ts`).
 *
 * An email (ADR-180) is a reply in core-api's email thread, or, from any other thread with a record,
 * an email to the contact from it, which joins their email thread or starts it: SMS and email are
 * two threads, so each tab asks about its own (`smsTarget`, `emailTarget`). As for a text, whether
 * it may leave is core-api's compose answer (`email-compose.tsx`). The subject is filled with the
 * one a reply takes ("Re: ..."), and can be changed; an email with none is not sent. An email carries
 * no attachments from here, and its answers are told as a text's are.
 *
 * WhatsApp and iMessage do not send yet: Send says so.
 */
export function Composer({
  conversation: c, live = false, preferChannel,
}: {
  conversation: Conversation;
  live?: boolean;
  /** The channel the page was opened for (`?channel=email`, the record's Email button), taken when
   *  the thread has somewhere for it to go. */
  preferChannel?: Channel;
}) {
  const toast = useToast();
  const addNote = useAddNote();
  const { contact: record } = React.useContext(RecordContext);
  const target = smsTarget(c, live);
  const mailTo = emailTarget(c, live);
  const [channel, setChannel] = React.useState<Channel>(() => initialComposerChannel(c, target, mailTo, preferChannel));
  const [text, setText] = React.useState("");
  // Null until the rep types one: the box shows the subject core-api gives a reply, as it arrives.
  const [subject, setSubject] = React.useState<string | null>(null);

  // A new thread starts a new reply. Not when the record's thread gives way to the conversation
  // core-api names for the same contact (the page opened from the record finds their thread, or
  // a colleague's first email makes one this reader may open) while something is typed: it is the
  // same reply, so it keeps its words, its subject and its channel.
  const opened = React.useRef({ id: c.id, leadId: c.leadId });
  React.useEffect(() => {
    const was = opened.current;
    opened.current = { id: c.id, leadId: c.leadId };
    const same = c.leadId !== null && was.leadId === c.leadId && was.id === `lead:${c.leadId}`;
    if (same && was.id !== c.id && text.trim()) return;
    setChannel(initialComposerChannel(c, target, mailTo, preferChannel));
    setText("");
    setSubject(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id]);

  // Each thread opened is a new visit, and the box is that visit's (the effect above empties it). A
  // send can answer after the rep has opened another thread, or gone and come back: the box holds
  // another reply by then, so the answer leaves it alone, and a toast about it names who it was to.
  const visit = React.useRef(0);
  React.useLayoutEffect(() => {
    visit.current += 1;
  }, [c.id]);

  // The record can turn out to be one the reader may not open after the thread has opened (core-api
  // answers it 404): a comment needs the record, so the box moves to the contact's channel, keeping
  // what was typed. Adjusted during the render, so no frame shows a comment it cannot save.
  const [leadId, setLeadId] = React.useState(c.leadId);
  if (leadId !== c.leadId) {
    setLeadId(c.leadId);
    if (c.leadId === null && channel === "comment") setChannel(initialComposerChannel(c, target, mailTo, preferChannel));
  }

  const comment = channel === "comment";
  const sms = channel === "sms" && target !== null;
  const email = channel === "email" && mailTo !== null;
  const compose = useSmsCompose(sms ? target : null);
  const mailCompose = useEmailComposeFor(email ? mailTo : null);
  const sending = useSendSms();
  const mailing = useSendEmailTo();
  // Media is uploaded to a conversation core-api already has; a contact's first text has no thread
  // to upload to yet, so the clip is not offered there. An email from here carries none.
  const attachTo = sms && target?.kind === "conversation" ? target.id : null;
  const files = useAttachments(attachTo, c.id);
  const mail = email ? mailCompose.data : undefined;
  // The subject a reply takes, until the rep types their own.
  const subjectValue = subject ?? mail?.subject ?? "";

  const blocked = blockedFor(c, channel, record);
  const reason =
    blocked ??
    composerReason(c, channel, channel === "email" ? mailTo : target) ??
    (sms
      ? compose.data
        ? composeRefusal(compose.data, formatDateTime)
        : compose.isError
          ? "Could not check whether a text can be sent. Try again in a moment."
          : "Checking whether a text can be sent…"
      : email
        ? mail
          ? composeRefusal({ ...mail, detail: mail.detail ?? "An email cannot be sent to this contact now." }) ??
            (subjectValue.trim() ? null : "Give the email a subject.")
          : mailCompose.isError
            ? "Could not check whether an email can be sent. Try again in a moment."
            : "Checking whether an email can be sent…"
        : null);
  const maxChars = sms && compose.data ? smsRoom(compose.data) : email ? mail?.max_chars : undefined;
  const optOutLine = sms ? compose.data?.opt_out_line : null;
  const pending = comment ? addNote.isPending : email ? mailing.isPending : sending.isPending;
  // Files belong to a text: picked on SMS, they wait there while another channel is open, and
  // nothing else carries or counts them.
  const attached = sms ? files.items.length : 0;
  const uploading = sms && files.uploading;
  // A caption-less picture is a legal MMS, so Send wants words or an attachment, not both.
  const empty = !text.trim() && attached === 0;

  const send = () => {
    const body = text.trim();
    // Held while an upload is in flight: core-api cannot tell a second request from a second text
    // (ADR-098), and a send that named a media id still arriving would be refused `media_not_ready`.
    if (reason !== null || empty || pending || uploading) return;
    const at = visit.current;
    const who = conversationTitle(c);
    const here = () => visit.current === at;
    const toWhom = () => (here() ? "" : ` to ${who}`);
    if (email && mailTo) {
      const clear = () => {
        if (here()) {
          setText("");
          setSubject(null);
        }
      };
      // Whatever the answer, the send asks core-api's compose again (`useSendEmail`, `useEmailContact`).
      mailing.send(mailTo, { text: body, subject: subjectValue.trim() }, {
        onSent: clear,
        onNotDelivered: (why) => {
          clear();
          toast.error(`The email${toWhom()} was not delivered`, why ?? "It is in the thread. Try sending it again.");
        },
        // Nothing was kept: what was typed stays, for another try.
        onRefused: (why) => toast.error(`The email${toWhom()} was not sent`, why),
        onUnconfirmed: () => {
          clear();
          toast.error(`We could not confirm the email${toWhom()} was sent`, "Check the thread before you send it again.");
        },
      });
      return;
    }
    if (sms && target) {
      const clear = () => {
        if (here()) {
          setText("");
          files.clear();
        }
      };
      // Whatever the answer, the send asks core-api's compose again (`useSendText`, `useTextContact`).
      sending.send(target, body, files.storedIds(), {
        onSent: clear,
        onNotDelivered: (why) => {
          clear();
          toast.error(`The text${toWhom()} was not delivered`, why ?? "It is in the thread. Try sending it again.");
        },
        // Nothing was kept: what was typed stays, for another try.
        onRefused: (why) => toast.error(`The text${toWhom()} was not sent`, why),
        onUnconfirmed: () => {
          clear();
          toast.error(`We could not confirm the text${toWhom()} was sent`, "Check the thread before you send it again.");
        },
      });
      return;
    }
    if (!comment || !c.leadId) return;
    addNote.mutate(
      { leadId: c.leadId, body },
      {
        onSuccess: () => setText(""),
        onError: (e) =>
          toast.error("Could not add the comment", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  };
  const Icon = CHANNEL_ICONS[channel];
  const phone = c.channel === "email" ? record?.phone ?? null : c.phone;
  const to = channel === "email" ? mail?.to_address ?? c.contactName ?? c.email : phone ? formatPhoneNumber(phone) : null;

  return (
    <div className={cn("border-t border-border p-3", comment && "bg-warning-2")}>
      <div
        className={cn(
          "rounded-lg border bg-surface focus-within:border-accent-8",
          comment ? "border-warning-6" : "border-border",
        )}
      >
        {channel === "email" && (
          <div className="space-y-1 border-b border-border px-3 py-2 text-xs">
            <p className="text-text-muted">
              To <span className="text-text">{to ?? "—"}</span>
            </p>
            <Input
              value={subjectValue}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              aria-label="Email subject"
              maxLength={mail?.max_subject_chars ?? 255}
              className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
            />
          </div>
        )}
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={comment ? "Write a note for your team" : `Message ${to ?? ""} by ${CHANNEL_LABELS[channel]}`}
          rows={channel === "email" ? 4 : 2}
          maxLength={comment ? 4000 : maxChars}
          className="min-h-0 resize-none border-0 shadow-none focus-visible:ring-0"
          aria-label={comment ? "Internal comment" : "Message"}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              send();
            }
          }}
        />
        {sms && compose.data?.can_send && compose.data.opt_out_line && (
          <p className="px-3 pb-1 text-2xs text-text-muted">
            The first text to this number ends with “{compose.data.opt_out_line}”
          </p>
        )}
        {attached > 0 && <AttachmentStrip files={files} />}
        <div className="flex items-center gap-2 px-2 pb-2">
          <ChannelPicker
            conversation={c}
            record={record}
            targets={{ sms: target, email: mailTo }}
            channel={channel}
            onChannel={setChannel}
          />
          {attachTo !== null && (
            <>
              <input
                ref={files.chooser}
                type="file"
                accept={MMS_ACCEPT}
                multiple
                className="hidden"
                tabIndex={-1}
                aria-hidden
                onChange={files.onChoose}
              />
              <Tooltip content={files.full ? `A text can carry ${MMS_MAX_FILES} files.` : "Attach a picture or PDF"}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={files.full || pending}
                  onClick={() => files.chooser.current?.click()}
                  aria-label="Attach a picture or PDF"
                >
                  <Paperclip />
                </Button>
              </Tooltip>
            </>
          )}
          <span className="min-w-0 flex-1 truncate text-2xs text-text-subtle">
            {comment
              ? "Only your team sees internal comments."
              : attached > 0
                ? files.totalLine
                : channel === "sms" && text
                  ? smsCount(text, maxChars, optOutLine)
                  : blocked ?? (sms && compose.data?.can_send && compose.data.from_number
                    ? `From ${formatPhoneNumber(compose.data.from_number)}`
                    : email && mail?.can_send && mail.from_address
                      ? emailFrom(mail.from_name, mail.from_address)
                      : "")}
          </span>
          <Tooltip content={reason ?? (uploading ? "Waiting for the files to finish uploading…" : null)}>
            <span tabIndex={reason ? 0 : undefined}>
              <Button
                variant="default"
                size="sm"
                disabled={reason !== null || empty || pending || uploading}
                loading={pending}
                onClick={send}
                aria-label={comment ? "Add comment" : "Send"}
              >
                <Icon className="text-white" />
                {comment ? "Comment" : "Send"}
                <Send />
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

function ChannelPicker({
  conversation, record, targets, channel, onChannel,
}: {
  conversation: Conversation;
  record: LeadCard | undefined;
  /** Each channel's own: an SMS thread's email goes to the record, an email thread's text too. */
  targets: { sms: SmsTarget | null; email: EmailTarget | null };
  channel: Channel;
  onChannel: (channel: Channel) => void;
}) {
  const Icon = CHANNEL_ICONS[channel];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={`Channel: ${CHANNEL_LABELS[channel]}`}>
          <Icon className={CHANNEL_TONES[channel]} />
          {CHANNEL_LABELS[channel]}
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Reply by</DropdownMenuLabel>
        {COMPOSER_CHANNELS.map((o) => {
          const ItemIcon = CHANNEL_ICONS[o.channel];
          const closed = blockedFor(conversation, o.channel, record);
          const off = composerReason(conversation, o.channel, o.channel === "email" ? targets.email : targets.sms);
          return (
            <DropdownMenuItem key={o.channel} onSelect={() => onChannel(o.channel)}>
              <ItemIcon className={CHANNEL_TONES[o.channel]} />
              <span className="flex-1">{o.label}</span>
              {(closed || off) && (
                <span className="ml-3 text-2xs text-text-subtle">{closed ? "Unavailable" : "Not connected"}</span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "42 / 1,600 · 1 segment": what is typed against the room core-api leaves for it (`smsRoom`), and
 *  the parts it travels in, as core-api counts them, with a first text's opt-out line under it. */
function smsCount(text: string, max: number | undefined, optOutLine: string | null | undefined): string {
  const parts = smsSegments(smsBody(text, optOutLine));
  const length = max ? `${text.length.toLocaleString()} / ${max.toLocaleString()}` : `${text.length} characters`;
  return `${length} · ${parts} segment${parts === 1 ? "" : "s"}`;
}

/** "From Injury Careline <help@example.com>": where an email leaves from, as the footer says it. */
function emailFrom(name: string | null, address: string): string {
  return name?.trim() ? `From ${name.trim()} <${address}>` : `From ${address}`;
}

/** core-api's answer to whether a text may leave now: the conversation's, or the contact's. */
function useSmsCompose(target: SmsTarget | null) {
  const conversation = useTextCompose(target?.kind === "conversation" ? target.id : undefined);
  const lead = useContactTextCompose(target?.kind === "lead" ? target.leadId : undefined);
  return target?.kind === "lead" ? lead : conversation;
}

/**
 * Sends an SMS to where the thread points, and says how it went: sent; kept but not delivered (the
 * provider refused it, or nobody knows: it is in the thread either way, marked); refused before it
 * was kept (a 409 with core-api's sentence, or another 4xx); or unconfirmed, when no answer came
 * back to say (`sendFailure`).
 */
function useSendSms() {
  const reply = useSendText();
  const first = useTextContact();
  // Held from the press, not from the mutation's next render: core-api cannot tell a second request
  // from a second text (ADR-098), so a double press in between must not send twice.
  const inFlight = React.useRef(false);
  const [holding, setHolding] = React.useState(false);
  const send = (
    target: SmsTarget,
    text: string,
    mediaIds: string[],
    on: {
      onSent: () => void;
      onNotDelivered: (why: string | null) => void;
      onRefused: (why: string) => void;
      onUnconfirmed: () => void;
    },
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setHolding(true);
    const done = () => {
      inFlight.current = false;
      setHolding(false);
    };
    const settled = (status: string, why: string | null) => {
      done();
      if (status === "failed") on.onNotDelivered(why);
      else on.onSent();
    };
    const failed = (e: unknown) => {
      done();
      const failure = sendFailure(e);
      if (failure.kind === "refused") on.onRefused(failure.detail);
      else on.onUnconfirmed();
    };
    if (target.kind === "conversation") {
      reply.mutate(
        { conversationId: target.id, text, mediaIds },
        { onSuccess: (m) => settled(m.status, m.error_detail ?? m.error), onError: failed },
      );
    } else {
      first.mutate({ leadId: target.leadId, text }, { onSuccess: (m) => settled(m.status, m.error), onError: failed });
    }
  };
  return { send, isPending: holding || reply.isPending || first.isPending };
}

/**
 * Why an upload failed, in words a rep can act on.
 *
 * core-api's own sentence is used where it gave one a person can use — a file too large, a kind
 * that cannot be texted. The two that are not the rep's fault say so instead of showing the
 * server's words: a 404 means this core-api has no upload route (a console deployed ahead of its
 * backend), and a 503 means the deployment has no store for pictures. "Not Found" on the strip
 * tells a rep nothing and reads like the file is missing, which it is not.
 */
function uploadProblem(error: unknown): string {
  if (!(error instanceof ApiError)) return "Could not upload this file. Remove it and try again.";
  if (error.status === 404 || error.status === 503) {
    return "Sending pictures is not set up on this system yet. Ask an admin.";
  }
  if (error.status === 413) {
    return "This file is too large to text. Choose a smaller one.";
  }
  if (error.status === 415) {
    return "This kind of file cannot be texted. Choose a picture or a PDF.";
  }
  return error.message || "Could not upload this file. Remove it and try again.";
}

/** One file on the strip: chosen, then uploading, then stored with core-api's id, or failed. */
interface Attachment {
  /** Local, so a file can be removed before core-api has given it an id. */
  key: string;
  name: string;
  size: number;
  type: string;
  /** An object URL for a picture's thumbnail; null for a PDF, which draws an icon. */
  preview: string | null;
  state: "uploading" | "stored" | "failed";
  /** core-api's media id, once stored: what the send names. */
  mediaId: string | null;
  /** Why the upload failed, in core-api's words where it gave any. */
  error: string | null;
}

/**
 * The composer's pending attachments: chosen, uploaded one by one as they are chosen, and named by
 * the send.
 *
 * Every size check is against the running total, not the file alone: Twilio caps a message and all
 * its media together (`lib/mms.ts`), and the strip shows that total so a rep sees the fourth photo
 * will not fit before they have waited for its upload.
 *
 * A failed file stays on the strip with its reason rather than disappearing, so the rep knows why
 * their picture is not going; it is not named by the send.
 */
function useAttachments(conversationId: string | null, threadKey: string) {
  const toast = useToast();
  const upload = useUploadConversationMedia();
  const chooser = React.useRef<HTMLInputElement>(null);
  const [items, setItems] = React.useState<Attachment[]>([]);
  // Held from the choice, not from the state React has rendered: several files chosen at once are
  // checked against each other's bytes before any upload has answered.
  const bytes = React.useRef(0);
  const nextKey = React.useRef(0);

  const forget = React.useCallback((keep: (a: Attachment) => boolean) => {
    setItems((current) => {
      const next = current.filter(keep);
      for (const gone of current) {
        if (!next.includes(gone) && gone.preview) URL.revokeObjectURL(gone.preview);
      }
      bytes.current = next.reduce((total, a) => total + a.size, 0);
      return next;
    });
  }, []);

  const clear = React.useCallback(() => forget(() => false), [forget]);

  // A new thread starts a new reply, with nothing attached to the old one's.
  React.useEffect(() => clear, [threadKey, clear]);

  const onChoose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    // Cleared so choosing the same file again, after removing another, still fires.
    event.target.value = "";
    if (conversationId === null) return;
    let count = items.length;
    for (const file of chosen) {
      const problem =
        count >= MMS_MAX_FILES
          ? `A text can carry ${MMS_MAX_FILES} files.`
          : mmsFileProblem(file, bytes.current);
      if (problem) {
        toast.error("That file cannot be attached", problem);
        continue;
      }
      const key = `a${nextKey.current++}`;
      count += 1;
      bytes.current += file.size;
      const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
      setItems((current) => [
        ...current,
        { key, name: file.name, size: file.size, type: file.type, preview, state: "uploading", mediaId: null, error: null },
      ]);
      const settle = (patch: Partial<Attachment>) =>
        setItems((current) => current.map((a) => (a.key === key ? { ...a, ...patch } : a)));
      upload
        .mutateAsync({ conversationId, file })
        .then((media) => settle({ state: "stored", mediaId: media.id }))
        .catch((e: unknown) => {
          // The bytes never left, so they must not count against the next file's room.
          bytes.current = Math.max(0, bytes.current - file.size);
          settle({
            state: "failed",
            error: uploadProblem(e),
          });
        });
    }
  };

  const storedIds = () => items.filter((a) => a.state === "stored" && a.mediaId).map((a) => a.mediaId as string);
  const attachedBytes = items.filter((a) => a.state !== "failed").reduce((total, a) => total + a.size, 0);

  return {
    chooser,
    items,
    onChoose,
    clear,
    remove: (key: string) => forget((a) => a.key !== key),
    storedIds,
    uploading: items.some((a) => a.state === "uploading"),
    full: items.length >= MMS_MAX_FILES,
    /** The running total, because the cap is on the message and its files together. */
    totalLine: `${items.length} file${items.length === 1 ? "" : "s"} · ${formatBytes(attachedBytes)} of ${formatBytes(MMS_TOTAL_MAX_BYTES)}`,
  };
}

/** The pending attachments above the composer's controls: each file, its size, and a way to take
 *  it off again. */
function AttachmentStrip({ files }: { files: ReturnType<typeof useAttachments> }) {
  return (
    <ul className="flex flex-wrap gap-2 border-t border-border px-2 py-2" aria-label="Attached files">
      {files.items.map((item) => (
        <li
          key={item.key}
          className={cn(
            "flex items-center gap-2 rounded-lg border bg-surface py-1 pl-1 pr-1.5 text-2xs",
            item.state === "failed" ? "border-danger-6 bg-danger-2 text-danger-11" : "border-border text-text-muted",
          )}
        >
          <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-md bg-surface-sunken">
            {item.state === "uploading" ? (
              <Loader2 className="size-3.5 animate-spin text-text-subtle" aria-hidden />
            ) : item.preview && item.state === "stored" ? (
              <img src={item.preview} alt="" className="size-full object-cover" />
            ) : (
              <FileText className="size-3.5 text-text-subtle" aria-hidden />
            )}
          </span>
          <span className="min-w-0">
            <span className="block max-w-36 truncate text-text">{item.name}</span>
            <span className="block">
              {item.state === "failed" ? item.error : item.state === "uploading" ? "Uploading…" : formatBytes(item.size)}
            </span>
          </span>
          <button
            type="button"
            onClick={() => files.remove(item.key)}
            className="grid size-5 shrink-0 place-items-center rounded-md text-text-subtle transition-colors hover:bg-surface-hover hover:text-text"
            aria-label={`Remove ${item.name}`}
          >
            <X className="size-3" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}
